import axios from "axios";
import { differenceWith, fromPairs, isArray, isEqual, set, startCase, toPairs } from "lodash";
import { produce } from "immer";

import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { caseTransformingAxios, requestDataToSnakeCase } from "@shared/v2/caseTransformingAxios";
import { getExternalApi } from "@shared/externalApi";

import { transformFormOptions, transformPerson, transformSources, validateForm } from "./helpers";
import { linkFractal, messageFractal } from "@shared/fractals";

export const INIT_ROUTING_NUMBER_MODAL = {
  phoneNumber: null,
  onSubmit: () => {},
};

const INIT_USER = {
  addressesAttributes: [],
  agentStatus: null,
  emailDetailsAttributes: [],
  isLead: false,
  loginUserAttributes: {
    active: true,
    role: "Agent",
    licenseId: null,
    showAgentProfilePage: false,
    showOnWebsiteRoster: false,
    customLinksAttributes: [],
    subdomain: null,
  },
  domain: "",
  phoneDetailsAttributes: [],
  personBioAttributes: { bio: "" },
  exportToRoster: "true",
};

const INIT_PERSON = {
  firstName: "",
  lastName: "",
  typeName: "lead",
  status: "new",
  isLead: true,
};

const getInitPerson = (modalType) => {
  if (modalType === "websiteProfile") {
    return {
      ...INIT_PERSON,
      websiteRosterPersonAgent: "true",
      isLead: false,
      leadType: "n/a",
      typeName: "collaborator",
    };
  }
  if (modalType === "lender") {
    return {
      ...INIT_PERSON,
      websiteRosterPreferredLender: "true",
      isLead: false,
      leadType: "n/a",
      typeName: "collaborator",
    };
  }
  return { ...INIT_PERSON };
};

const initialState = {
  avatarLoading: false,
  customDateOptions: {},
  formOptions: {},
  formOptionsLoading: true,
  loading: false,
  originalPerson: {},
  person: {},
  errors: [],
  socialMediaErrors: {},
  showPhoneContext: null,
  routingNumberModal: INIT_ROUTING_NUMBER_MODAL,
};

const addPrefilledValues = (person, prefilledValues) => {
  if (!prefilledValues) return person;
  return {
    ...person,
    firstName: person.firstName || prefilledValues.firstName,
    lastName: person.lastName || prefilledValues.lastName,
    phoneDetailsAttributes: [
      ...(person.phoneDetailsAttributes || []),
      ...(prefilledValues.phoneDetailsAttributes || []).map((p, i) => ({
        ...p,
        position: (person.phoneDetailsAttributes || []).length + i + 1,
      })),
    ],
    emailDetailsAttributes: [
      ...(person.emailDetailsAttributes || []),
      ...(prefilledValues.emailDetailsAttributes || []).map((e, i) => ({
        ...e,
        position: (person.emailDetailsAttributes || []).length + i + 1,
      })),
    ],
  };
};

const validPositionFinder = (position) => (customLink) =>
  customLink.position === position && !("_destroy" in customLink);

const getFormData = createAsyncThunk(
  "contacts/getFormOptions",
  ({ modalType, personId, person, prefilledValues }, thunkApi) => {
    const { messagingServiceUrl } = thunkApi.getState().layout.apiUrls;
    const messagingApi = getExternalApi(messagingServiceUrl);
    const getTotalConversationCount = (personUuid, phoneNumber) =>
      messagingApi
        .get(`/Conversation/SmsAndGroupSmsCountForPersonAddressPair/${personUuid}/${phoneNumber}`, {
          signal: thunkApi.signal,
        })
        .then((res) => res.data.total)
        .catch(() => 0);
    const personPromise = personId
      ? axios.get(`/api/v4/${modalType === "user" ? "account_people" : "contact_people"}/${personId}`, {
          signal: thunkApi.signal,
        })
      : Promise.resolve({ data: modalType === "user" ? INIT_USER : person || getInitPerson(modalType) });
    const augmentedPhoneDetailsPersonPromise = personPromise
      .then((res) => transformPerson(res.data))
      .then(async (person) => {
        if (!person) return person;
        const { phoneDetailsAttributes = [] } = person;
        const modifiedDetailsAttributes = await Promise.all(
          phoneDetailsAttributes.map((phone) =>
            getTotalConversationCount(person.uuid, phone.value).then((archiveConversationsCount) => ({
              ...phone,
              archiveConversationsCount,
            })),
          ),
        ).catch(() => phoneDetailsAttributes);
        return { ...person, phoneDetailsAttributes: modifiedDetailsAttributes };
      });
    return Promise.all([
      augmentedPhoneDetailsPersonPromise,
      caseTransformingAxios
        .get(
          modalType === "user"
            ? "/api/v4/account_people/form_options"
            : "/api/v4/contact_people/form_options",
          { signal: thunkApi.signal },
        )
        .then((res) => transformFormOptions(modalType === "user" ? res.data.formOptions : res.data)),
      caseTransformingAxios.get("/sources").then((res) => transformSources(res.data.sources)),
    ])
      .then(([person, formOptions, sourceOptions]) => ({
        person,
        prefilledValues,
        formOptions: {
          ...formOptions,
          sourceOptions,
        },
      }))
      .catch((err) => thunkApi.rejectWithValue(err.response?.data));
  },
);

const upsertPerson = createAsyncThunk("contacts/savePerson", ({ modalType }, thunkApi) => {
  const { originalPerson, person } = thunkApi.getState().contactsReducer;
  const response = validateForm(person, modalType);
  if (response.errors.length > 0) return Promise.reject(thunkApi.rejectWithValue(response));

  let savePerson = originalPerson.id
    ? fromPairs(differenceWith(toPairs(person), toPairs(originalPerson), isEqual))
    : person;

  const phoneChanges = originalPerson.id
    ? differenceWith(
        person.phoneDetailsAttributes,
        originalPerson.phoneDetailsAttributes,
        (a, b) => (a.value || "").replace(/\D/g, "") === (b.value || "").replace(/\D/g, ""),
      )
    : [];

  const changedPhoneDetailsAttributes = phoneChanges.filter((phone) => phone.archiveConversationsCount > 0);
  if (!person.phoneRemovalAck && changedPhoneDetailsAttributes.length > 0) {
    const changedNumbers = changedPhoneDetailsAttributes.map((phone) => {
      return {
        currentNumber: phone._destroy ? null : phone.value,
        previousNumber: originalPerson.phoneDetailsAttributes?.find(
          (originalPhone) => originalPhone.id === phone.id,
        )?.value,
      };
    });

    const conversationCount = changedPhoneDetailsAttributes.reduce(
      (acc, phone) => acc + phone.archiveConversationsCount,
      0,
    );

    return Promise.reject(
      thunkApi.rejectWithValue({
        type: "showPhoneContext",
        changedNumbers,
        conversationCount,
      }),
    );
  }

  if (savePerson?.websiteDisplayPhoneDetailsAttributes) {
    savePerson = produce(savePerson, (draft) => {
      const phoneNums = draft.websiteDisplayPhoneDetailsAttributes;
      Object.keys(phoneNums).forEach((type) => {
        if (phoneNums[type]) delete phoneNums[type].tempId;
      });
    });
  }

  // filter out mlses that are empty
  if (savePerson?.loginUserAttributes?.usersMlsesAttributes) {
    savePerson = produce(savePerson, (draft) => {
      draft.loginUserAttributes.usersMlsesAttributes = draft.loginUserAttributes.usersMlsesAttributes.filter(
        (item) => item.mlsId !== null,
      );
    });
  }

  return axios[person.id ? "put" : "post"](
    `/api/v4/${modalType === "user" ? "account_people" : "contact_people"}${person.id ? `/${person.id}` : ""}`,
    {
      person: {
        ...requestDataToSnakeCase({
          ...savePerson,
          phoneDetailsAttributes: person.phoneDetailsAttributes,
          phoneRemovalAck: person.phoneRemovalAck || person.phoneDetailsAttributes?.some((d) => d._destroy),
        }),
        milestones: savePerson.milestones,
      },
    },
    {
      headers: {
        "x-csrf-token": ReactOnRails.authenticityToken(),
      },
      signal: thunkApi.signal,
    },
  )
    .then((res) => transformPerson(res.data))
    .catch((err) => thunkApi.rejectWithValue(err.response?.data));
});

const uploadAvatar = createAsyncThunk("contacts/uploadAvatar", ({ file, personId }, thunkApi) => {
  const formData = new FormData();
  formData.append("authenticity_token", ReactOnRails.authenticityToken());
  formData.append("person[avatar]", file);
  return caseTransformingAxios
    .post(`/api/v4/person_detail/upload_avatar/${personId}`, formData, { signal: thunkApi.signal })
    .then((res) => res.data)
    .catch((err) => thunkApi.rejectWithValue(err.response?.data));
});

const contactsReducer = createSlice({
  initialState,
  name: "contacts",
  reducers: {
    setErrors: (state, { payload = [] }) => {
      state.errors = payload;
    },
    setPerson: (state, { payload: { originalPerson, person } }) => {
      if (originalPerson) state.originalPerson = originalPerson;
      if (person) state.person = person;
    },
    setPersonValue: (state, { payload }) => {
      set(state.person, payload.key, payload.value);
    },
    setShowPhoneContext: (state, { payload }) => {
      state.showPhoneContext = payload;
    },
    setShowRemoveRoutingWarning: (state, { payload }) => {
      state.routingNumberModal = payload;
    },
    setSocialMediaError: (state, { payload: { key, error } }) => {
      if (error) {
        state.socialMediaErrors[key] = error;
      } else {
        delete state.socialMediaErrors[key];
      }
    },
    addCustomLink: (state, { payload }) => {
      state.person.customLinksAttributes = isArray(state.person.customLinksAttributes)
        ? state.person.customLinksAttributes
        : [];

      const { customLinksAttributes } = state.person;
      customLinksAttributes.push({
        meta: {
          icon: "fa6 fa6-link",
        },
        position: customLinksAttributes.filter((customLink) => !customLink._destroy).length + 1,
        category: "custom_link",
        name: "",
        value: "",
        ...payload,
      });
    },
    setCustomLink: (state, { payload }) => {
      const { customLinksAttributes } = state.person;
      const customLinkIndex = customLinksAttributes.findIndex(validPositionFinder(payload.position));
      customLinksAttributes[customLinkIndex] = { ...customLinksAttributes[customLinkIndex], ...payload };
    },
    deleteCustomLink: (state, { payload }) => {
      const { customLinksAttributes } = state.person;
      const customLinkIndex = customLinksAttributes.findIndex(validPositionFinder(payload.position));
      customLinksAttributes[customLinkIndex]._destroy = true;
      customLinksAttributes
        .filter((customLink) => customLink._destroy)
        .forEach((customLink, i) => {
          customLink.position = -i - 1;
        });
      customLinksAttributes
        .filter((customLink) => !customLink._destroy)
        .forEach((customLink, i) => {
          customLink.position = i + 1;
        });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getFormData.pending, (state) => {
        state.formOptionsLoading = true;
        state.errors = [];
        state.socialMediaErrors = {};
      })
      .addCase(getFormData.fulfilled, (state, { payload }) => {
        if (payload.person) {
          state.originalPerson = payload.person;
          const newPerson = { ...payload.person };

          state.person = addPrefilledValues(newPerson, payload.prefilledValues);
        }
        state.formOptions = payload.formOptions;
        state.formOptionsLoading = false;
      })
      .addCase(getFormData.rejected, (state) => {
        state.errors = ["Failed to load form options. Please try again later."];
        state.formOptionsLoading = false;
      });

    builder
      .addCase(upsertPerson.pending, (state) => {
        state.errors = [];
        state.loading = true;
      })
      .addCase(upsertPerson.fulfilled, (state, { payload }) => {
        state.originalPerson = payload;
        state.person = payload;
        state.loading = false;
      })
      .addCase(upsertPerson.rejected, (state, { payload, ...props }) => {
        if (payload?.type === "showPhoneContext") {
          state.showPhoneContext = payload;
          state.loading = false;
          return;
        }

        const errBody = payload?.errors?.[0] || {};
        let errors = [];

        let phoneValidations = payload?.errors?.[0]?.context?.phone_validations;
        let emailValidations = payload?.errors?.[0]?.context?.email_validations;

        if (phoneValidations?.length > 0) {
          const messageError = messageFractal(phoneValidations.length, "Mobile phone number");
          const linksError = phoneValidations
            .map((validation) => linkFractal(validation.url, validation.name))
            .join(", ");

          errors.push(`${messageError}${linksError}`);
        }

        if (emailValidations?.length > 0) {
          const acctDupes = Object.entries(emailValidations[0]);
          const messageError = messageFractal(acctDupes.length, "Email");
          const linksError = acctDupes
            .map(([slug, fullName]) => linkFractal(`/people/${slug}`, fullName))
            .join(", ");

          errors.push(`${messageError}${linksError}`);
        }

        if (typeof errBody.detail === "object") {
          Object.entries(errBody?.detail).forEach(([key, value]) =>
            errors.push(`${startCase(key.split(".")?.[0])}: ${value}`),
          );
        }

        if (errors.length === 0) errors = ["Failed to save person. Please try again later."];

        state.errors = errors;
        state.loading = false;
      });

    builder
      .addCase(uploadAvatar.pending, (state) => {
        state.avatarLoading = true;
        state.loading = true;
      })
      .addCase(uploadAvatar.fulfilled, (state, { payload }) => {
        state.person.avatarUrl = payload.data.attributes.avatarUrl;
        state.avatarLoading = false;
        state.loading = false;
      })
      .addCase(uploadAvatar.rejected, (state, { payload }) => {
        let errors = [];
        Object.entries(payload.errors?.[0]?.detail || {}).forEach(([key, value]) =>
          errors.push(`${startCase(key.split(".")?.[0])}: ${value}`),
        );
        if (errors.length === 0) errors = ["Failed to upload profile photo. Please try again later."];

        state.errors = errors;
        state.avatarLoading = false;
        state.loading = false;
      });
  },
});

export { getFormData, upsertPerson, uploadAvatar };

export const {
  addCustomLink,
  setCustomLink,
  deleteCustomLink,
  setErrors,
  setPerson,
  setPersonValue,
  setShowPhoneContext,
  setShowRemoveRoutingWarning,
  setSocialMediaError,
} = contactsReducer.actions;

export default contactsReducer.reducer;
